import { ApolloLink, Observable } from '@apollo/client';
import type { Operation, FetchResult, NextLink } from '@apollo/client';

/**
 * Handler function that can conditionally override a resolved field value.
 * Return the overridden value, or undefined to use the resolver's value.
 */
export type FieldOverrideHandler<
    ParentType = any,
    ResolvedValueType = any,
    ContextType = any
> = (args: {
    /** The parent object containing the field */
    parent: ParentType;
    /** The field name being resolved */
    fieldName: string;
    /** The value returned by the resolver */
    resolvedValue: ResolvedValueType;
    /** The operation context */
    context: ContextType;
    /** The GraphQL operation */
    operation: Operation;
}) => ResolvedValueType | undefined;

/**
 * Registry for field override handlers.
 * Maps field paths (e.g., "OneGQL_Person.presence") to handler functions.
 */
class FieldOverrideRegistry {
    private handlers = new Map<string, FieldOverrideHandler>();

    /**
     * Register a handler for a specific field.
     * @param typeName The GraphQL type name (e.g., "OneGQL_Person", "OneGQL_Me")
     * @param fieldName The field name (e.g., "presence")
     * @param handler The handler function
     */
    register(typeName: string, fieldName: string, handler: FieldOverrideHandler): void {
        const key = `${typeName}.${fieldName}`;
        this.handlers.set(key, handler);
    }

    /**
     * Unregister a handler for a specific field.
     */
    unregister(typeName: string, fieldName: string): void {
        const key = `${typeName}.${fieldName}`;
        this.handlers.delete(key);
    }

    /**
     * Get a handler for a specific field.
     */
    get(typeName: string, fieldName: string): FieldOverrideHandler | undefined {
        const key = `${typeName}.${fieldName}`;
        return this.handlers.get(key);
    }

    /**
     * Returns true if any handlers are registered.
     */
    hasHandlers(): boolean {
        return this.handlers.size > 0;
    }

    /**
     * Clear all handlers.
     */
    clear(): void {
        this.handlers.clear();
    }
}

// Global registry instance (lazy initialized)
let fieldOverrideRegistry: FieldOverrideRegistry | undefined;

function getFieldOverrideRegistry(): FieldOverrideRegistry {
    if (!fieldOverrideRegistry) {
        fieldOverrideRegistry = new FieldOverrideRegistry();
    }
    return fieldOverrideRegistry;
}

/**
 * Register a field override handler on the main thread.
 * This allows you to conditionally override values resolved by worker thread resolvers.
 *
 * @example
 * ```typescript
 * // Override presence based on main thread state
 * registerFieldOverride<OneGql_Person>('OneGQL_Person', 'presence', ({ parent, resolvedValue }) => {
 *     const mainThreadPresence = getPresence(parent.id);
 *     if (mainThreadPresence !== undefined) {
 *         return mainThreadPresence;
 *     }
 *     return undefined; // Use resolver's value
 * });
 * ```
 *
 * @template TParent The GraphQL parent type (e.g., OneGql_Person)
 * @template TFieldName The field name to override (type-checked against TParent's fields)
 * @param typeName The GraphQL __typename value (e.g., 'OneGQL_Person')
 * @param fieldName The field name to override (type-checked against TParent)
 * @param handler The override handler function
 */
export function registerFieldOverride<
    TParent extends {
        __typename?: string;
    },
    TFieldName extends Exclude<keyof TParent, '__typename'> & string = Exclude<
        keyof TParent,
        '__typename'
    > &
        string
>(
    typeName: TParent['__typename'] & string,
    fieldName: TFieldName,
    handler: FieldOverrideHandler<TParent, TParent[TFieldName], any>
): void {
    getFieldOverrideRegistry().register(typeName, fieldName, handler);
}

/**
 * Unregister a field override handler.
 */
export function unregisterFieldOverride(typeName: string, fieldName: string): void {
    getFieldOverrideRegistry().unregister(typeName, fieldName);
}

/**
 * Clear all field override handlers.
 */
export function clearFieldOverrides(): void {
    getFieldOverrideRegistry().clear();
}

/**
 * Apollo Link that processes GraphQL responses and applies field overrides
 * from handlers registered on the main thread.
 */
export function createFieldOverrideLink(): ApolloLink {
    return new ApolloLink((operation: Operation, forward: NextLink) => {
        return new Observable<FetchResult>(observer => {
            const subscription = forward(operation).subscribe({
                next: (result: FetchResult) => {
                    // Process the result and apply any field overrides
                    const registry = getFieldOverrideRegistry();
                    if (result.data && registry.hasHandlers()) {
                        const overriddenData = applyFieldOverrides(
                            result.data,
                            operation,
                            registry
                        );
                        observer.next({ ...result, data: overriddenData });
                    } else {
                        observer.next(result);
                    }
                },
                error: error => {
                    observer.error(error);
                },
                complete: () => {
                    observer.complete();
                },
            });

            return () => {
                subscription.unsubscribe();
            };
        });
    });
}

/**
 * Recursively traverse the GraphQL response data and apply field overrides.
 */
function applyFieldOverrides(
    data: any,
    operation: Operation,
    registry: FieldOverrideRegistry
): any {
    if (!data || typeof data !== 'object') {
        return data;
    }

    if (Array.isArray(data)) {
        return data.map(item => applyFieldOverrides(item, operation, registry));
    }

    const result: any = {};
    const typeName = data.__typename;

    for (const [key, value] of Object.entries(data)) {
        if (key === '__typename') {
            result[key] = value;
            continue;
        }

        // Check if there's a handler for this field
        let overriddenValue = value;
        if (typeName) {
            const handler = registry.get(typeName, key);
            if (handler) {
                const override = handler({
                    parent: data,
                    fieldName: key,
                    resolvedValue: value,
                    context: operation.getContext(),
                    operation,
                });

                // Only override if handler returns a defined value
                if (override !== undefined) {
                    overriddenValue = override;
                }
            }
        }

        // Recursively process nested objects/arrays
        if (overriddenValue && typeof overriddenValue === 'object') {
            result[key] = applyFieldOverrides(overriddenValue, operation, registry);
        } else {
            result[key] = overriddenValue;
        }
    }

    return result;
}
